import React, { useState } from "react";
import OTPInput from "react-otp-input";
import { Spinner } from "reactstrap";
import { CustomSnackbar } from "../../../helpers/CustomSnackbar";
import { Link, useNavigate } from "react-router-dom";
import { apiErrorHandler } from "../../../helpers/common";
import {
  verifyMobileForgotPasswordOtp,
  forgetPassword,
  resetPassword,
} from "../../../api/customer";
import PropTypes from "prop-types";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const VerifyMobile = ({ formData, showLoginBtn = true }) => {
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(60);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // ✅ State variables for handling Password Reset phase
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const navigate = useNavigate();

  React.useEffect(() => {
    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleVerifyMobile = async () => {
    if (!otp) {
      CustomSnackbar.error("Please enter OTP!");
      return;
    }

    if (otp?.length < 6) {
      CustomSnackbar.error("Invalid OTP!");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        phoneNumber: formData?.phoneNumber,
        otp: otp,
      };

      const response = await verifyMobileForgotPasswordOtp(payload);

      if (response?.data?.success || response?.data?.message) {
        CustomSnackbar.success(
          response?.data?.message || "OTP verified successfully!",
        );

        // Save the resetToken and switch view or navigate to New Password page
        if (response?.data?.resetToken) {
          setResetToken(response.data.resetToken);
          setIsOtpVerified(true);
        } else {
          // Pass both phoneNumber and resetToken to RetypePassword page via router state
          navigate("/RetypePassword", {
            state: {
              phoneNumber: formData?.phoneNumber,
              resetToken: response?.data?.resetToken || "",
            },
          });
        }
      }
    } catch (error) {
      apiErrorHandler(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (remainingTime > 0) return;

    setResendLoading(true);
    try {
      const response = await forgetPassword({
        phoneNumber: formData?.phoneNumber,
      });
      CustomSnackbar.success(
        response?.data?.message || "OTP sent successfully!",
      );
      setRemainingTime(60);
    } catch (error) {
      apiErrorHandler(error);
    } finally {
      setResendLoading(false);
    }
  };

  // ✅ Submits reset password payload including phoneNumber
  const handleResetPassword = async () => {
    if (!newPassword) {
      CustomSnackbar.error("Please enter a new password!");
      return;
    }

    if (newPassword.length < 6) {
      CustomSnackbar.error("Password must be at least 6 characters long!");
      return;
    }

    if (newPassword !== confirmPassword) {
      CustomSnackbar.error("Passwords do not match!");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        phoneNumber: formData?.phoneNumber,
        resetToken: resetToken,
        password: newPassword,
        retypePassword: confirmPassword,
        newPassword: newPassword,
        confirmPassword: confirmPassword,
      };

      const response = await resetPassword(payload);

      if (response?.data?.message || response?.data?.success) {
        CustomSnackbar.success(
          response?.data?.message || "Password reset successfully!",
        );
        navigate("/signin");
      }
    } catch (error) {
      apiErrorHandler(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-100 verification-container">
      <div className="verification-card">
        {!isOtpVerified ? (
          <>
            <div className="text-center mb-4">
              <h4 className="verification-title">Verify Mobile OTP</h4>
              <p className="verification-subtitle">
                An OTP has been sent to your mobile number{" "}
                <b>{formData?.phoneNumber}</b>.
              </p>
            </div>

            <div className="otp-container">
              <OTPInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                renderSeparator={<span style={{ margin: 4 }}>-</span>}
                containerStyle={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "nowrap",
                  gap: "3px",
                  marginBottom: "15px",
                  padding: "0 5px",
                  maxWidth: "100%",
                }}
                inputType="number"
                renderInput={(props) => (
                  <input
                    {...props}
                    className={`otp-input`}
                    style={{ flexShrink: 0 }}
                  />
                )}
              />
            </div>

            <div className="resend-otp-container">
              <div
                style={{
                  textAlign: "center",
                  marginTop: 10,
                  fontSize: 14,
                  color: "#fff",
                }}
              >
                {remainingTime === 0 ? (
                  <div
                    onClick={handleResendOTP}
                    style={{
                      cursor: "pointer",
                      display: "inline-flex",
                      gap: 6,
                      alignItems: "center",
                    }}
                  >
                    {resendLoading && <Spinner size="sm" />} Resend OTP
                  </div>
                ) : (
                  <>
                    You can resend the OTP in
                    <span> {remainingTime} secs</span>
                  </>
                )}
              </div>
            </div>

            <div className="text-center mt-4">
              {isLoading ? (
                <button
                  className="verify-btn w-50 disabled"
                  type="button"
                  disabled
                >
                  <Spinner size="sm" className="me-2" />
                  Verify Mobile
                </button>
              ) : (
                <button
                  className="verify-btn w-50"
                  type="button"
                  onClick={handleVerifyMobile}
                >
                  Verify Mobile
                </button>
              )}
            </div>
          </>
        ) : (
          /* Render New Password and Confirm Password fields after OTP verification */
          <>
            <div className="text-center mb-4">
              <h4 className="verification-title">Set New Password</h4>
              <p className="verification-subtitle">
                Enter your new password below to reset your account credentials.
              </p>
            </div>

            <div className="mb-3 position-relative">
              <input
                type={showNewPassword ? "text" : "password"}
                className="form-control pe-5"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <span
                onClick={() => setShowNewPassword((prev) => !prev)}
                style={{
                  position: "absolute",
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  zIndex: 10,
                  color: "#6c757d",
                }}
              >
                {showNewPassword ? (
                  <FaEyeSlash size={18} />
                ) : (
                  <FaEye size={18} />
                )}
              </span>
            </div>

            <div className="mb-3 position-relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="form-control pe-5"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <span
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                style={{
                  position: "absolute",
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  zIndex: 10,
                  color: "#6c757d",
                }}
              >
                {showConfirmPassword ? (
                  <FaEyeSlash size={18} />
                ) : (
                  <FaEye size={18} />
                )}
              </span>
            </div>

            <div className="text-center mt-4">
              {isLoading ? (
                <button
                  className="verify-btn w-50 disabled"
                  type="button"
                  disabled
                >
                  <Spinner size="sm" className="me-2" />
                  Reset Password
                </button>
              ) : (
                <button
                  className="verify-btn w-50"
                  type="button"
                  onClick={handleResetPassword}
                >
                  Reset Password
                </button>
              )}
            </div>
          </>
        )}

        {showLoginBtn && (
          <div className="text-center mt-3">
            <Link to="/signin" className="resend-otp-button">
              Go to login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

VerifyMobile.propTypes = {
  formData: PropTypes.object,
  showLoginBtn: PropTypes.bool,
};

export default VerifyMobile;
